import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SharedServiceService } from '../shared-service.service';
import { FavoriteBlendState, FavoriteV1Row } from './favorite.models';

@Injectable({ providedIn: 'root' })
export class FavoriteSerializerService {

  constructor(private sharedService: SharedServiceService) {}

  /**
   * Captures current state and encodes it using React's V1 favorites contract.
   * Returns null when the blend contains no selected component data.
   */
  async buildV1Payload(slotNumber: string, shareableLink: string): Promise<string | null> {
    const blendState = await this.captureCurrentState();
    if (!this.hasMeaningfulData(blendState)) return null;
    return this.encodeV1Payload(slotNumber, blendState, shareableLink);
  }

  /** Pure V1 encoder matching React's section order and Object.entries formatting. */
  encodeV1Payload(slotNumber: string, state: FavoriteBlendState, shareableLink: string): string {
    const sections: FavoriteV1Row[][] = [
      state.finishedOilData,
      this.toReactPackageRows(state.package1Pred1),
      this.toReactPackageRows(state.package1Pred2),
      this.toReactPackageRows(state.package2Pred1),
      this.toReactPackageRows(state.package2Pred2),
      [{ wtper11: state.wtPer11 }],
      [{ wtper12: state.wtPer12 }],
      [{ wtper21: state.wtPer21 }],
      [{ wtper22: state.wtPer22 }],
      [{ dil11: state.dilPer11 }],
      [{ dil12: state.dilPer12 }],
      [{ dil21: state.dilPer21 }],
      [{ dil22: state.dilPer22 }],
      state.modInpDi,
      state.modInpVm,
      state.modInpPpd,
      state.modInpBo,
      this.toReactColumnRows(state.modInpColVm),
      this.toReactColumnRows(state.modInpColBo),
      this.toReactColumnRows(state.modInpColDi),
      state.modInpDi2,
      state.modInpVm2,
      state.modInpPpd2,
      state.modInpBo2,
      this.toReactColumnRows(state.modInpColVm2),
      this.toReactColumnRows(state.modInpColBo2),
      this.toReactColumnRows(state.modInpColDi2),
      [{ link: shareableLink }],
    ];

    const rows: Array<FavoriteV1Row | string> = [{ snum: slotNumber }];
    sections.forEach(section => rows.push('$', ...section));
    rows.push('@');

    return rows.map(row => this.serializeV1Row(row)).join('\n');
  }

  /**
   * Translates DI package prediction rows from Angular's internal shape
   * (component/treatRateRaw/unit/...) into React's display-key schema so the
   * React importer (which maps rows by field name) can read them. Rows already
   * in React shape pass through unchanged.
   */
  private toReactPackageRows(rows: FavoriteV1Row[]): FavoriteV1Row[] {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => {
      const r = row as Record<string, unknown>;
      const pick = (...keys: string[]): unknown => {
        for (const key of keys) {
          const value = r[key];
          if (value != null && value !== '') return value;
        }
        return null;
      };
      return {
        'Additive': pick('Additive', 'component'),
        'Dosage': pick('Dosage', 'treatRateRaw', 'treatRate'),
        'Unit': pick('Unit', 'unit'),
        'Family': pick('Family', 'family'),
        'Wt% in Finished Oil': pick('Wt% in Finished Oil', 'wtInFO'),
        'Wt% in DI Package': pick('Wt% in DI Package', 'wtInDI'),
        'Metal': pick('Metal', 'blendMetal'),
        'Typ. Metal %': pick('Typ. Metal %', 'Typ_Metal', 'typical'),
      };
    });
  }

  /**
   * Translates model-input column definitions into React's { field, header }
   * shape. Angular stores these as bare column-name strings (Object.keys of the
   * response row); serializing a string directly would explode it into
   * character-indexed entries. Objects already carrying field/header pass through.
   */
  private toReactColumnRows(rows: FavoriteV1Row[]): FavoriteV1Row[] {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => {
      if (typeof row === 'string') return { field: row, header: row };
      const r = row as Record<string, unknown>;
      if (r['field'] != null || r['header'] != null) {
        const field = r['field'] ?? r['header'];
        const header = r['header'] ?? r['field'];
        return { field, header };
      }
      return row;
    });
  }

  private serializeV1Row(row: FavoriteV1Row | string): string {
    return Object.entries(row)
      .map(([key, rawValue]) => {
        const value = typeof rawValue === 'number' ? rawValue.toFixed(3) : rawValue;
        return value != null ? `${key}: ${String(value)}` : 'null';
      })
      .join(',');
  }

  private hasMeaningfulData(state: FavoriteBlendState): boolean {
    const sections = [
      state.finishedOilData,
      state.package1Pred1,
      state.package1Pred2,
      state.package2Pred1,
      state.package2Pred2,
    ];
    return sections.some(rows => rows.some(row =>
      Object.entries(row).some(([key, value]) =>
        key !== 'Fixed_value' && value != null && String(value).trim() !== '' && value !== 'null'
      )
    ));
  }

  private hasValue(value: unknown): boolean {
    return value != null && value !== '';
  }

  /**
   * Applies a FavoriteBlendState to SharedService (used when importing a favorite).
   */
  applyStateFromPayload(state: FavoriteBlendState): void {
    this.sharedService.set_finishedoil_data(state.finishedOilData);
    this.sharedService.setPackage1Pred1(state.package1Pred1);
    this.sharedService.setPackage1Pred2(state.package1Pred2);
    this.sharedService.setPackage2Pred1(state.package2Pred1);
    this.sharedService.setPackage2Pred2(state.package2Pred2);

    // Wt% totals
    if (state.wtPer11 != null) this.sharedService.settotal_package_1_pred_1(parseFloat(state.wtPer11));
    if (state.wtPer12 != null) this.sharedService.settotal_package_1_pred_2(parseFloat(state.wtPer12));
    if (state.wtPer21 != null) this.sharedService.settotal_package_2_pred_1(parseFloat(state.wtPer21));
    if (state.wtPer22 != null) this.sharedService.settotal_package_2_pred_2(parseFloat(state.wtPer22));

    // Dil% (fo_dill_percent)
    if (state.dilPer11 != null) this.sharedService.set_Dil_wt_percent_Pack1_pred1(state.dilPer11);
    if (state.dilPer12 != null) this.sharedService.set_Dil_wt_percent_Pack1_pred2(state.dilPer12);
    if (state.dilPer21 != null) this.sharedService.set_Dil_wt_percent_Pack2_pred1(state.dilPer21);
    if (state.dilPer22 != null) this.sharedService.set_Dil_wt_percent_Pack2_pred2(state.dilPer22);

    // Model inputs prediction 1
    this.sharedService.set_mod_inp_di(state.modInpDi);
    this.sharedService.set_mod_inp_vm(state.modInpVm);
    this.sharedService.set_mod_inp_ppd(state.modInpPpd);
    this.sharedService.set_mod_inp_bo(state.modInpBo);
    this.sharedService.set_mod_inp_col_vm(state.modInpColVm);
    this.sharedService.set_mod_inp_col_bo(state.modInpColBo);
    this.sharedService.set_mod_inp_col_di(state.modInpColDi);

    // Model inputs prediction 2
    this.sharedService.set_mod_inp_di2(state.modInpDi2);
    this.sharedService.set_mod_inp_vm2(state.modInpVm2);
    this.sharedService.set_mod_inp_ppd2(state.modInpPpd2);
    this.sharedService.set_mod_inp_bo2(state.modInpBo2);
    this.sharedService.set_mod_inp_col_vm2(state.modInpColVm2);
    this.sharedService.set_mod_inp_col_bo2(state.modInpColBo2);
    this.sharedService.set_mod_inp_col_di2(state.modInpColDi2);

    // Mark imported model inputs authoritative per blend; empty means old favorite -> derive fresh.
    const hasMi1 = (state.modInpDi?.length || state.modInpVm?.length || state.modInpBo?.length || 0) > 0;
    const hasMi2 = (state.modInpDi2?.length || state.modInpVm2?.length || state.modInpBo2?.length || 0) > 0;
    this.sharedService.setModelInputsImported(1, hasMi1);
    this.sharedService.setModelInputsImported(2, hasMi2);

    // Signal import completed
    this.sharedService.setImpFavoriteFlag(true);
  }

  /**
   * Builds finishedOilData from live component observables instead of the stale
   * finishedoil_data$ snapshot (which only gets set during import).
   */
  private buildFinishedOilDataFromLive(
    baseOilRows: any[],
    vmRows: any[],
    ppdRows: any[],
    packageTotals: { pkg1TreatP1: any; pkg1TreatP2: any; pkg2TreatP1: any; pkg2TreatP2: any },
    packageNames: { pkg1: string | null; pkg2: string | null },
  ): Record<string, string>[] {
    const foData: Record<string, string>[] = [];

    // Package 1
    const pkg1: Record<string, string> = { Fixed_value: 'Package 1' };
    if (packageNames.pkg1) {
      pkg1['Name'] = packageNames.pkg1;
      if (this.hasValue(packageTotals.pkg1TreatP1)) pkg1['Treat_wt_P1'] = String(packageTotals.pkg1TreatP1);
      if (this.hasValue(packageTotals.pkg1TreatP2)) pkg1['Treat_wt_P2'] = String(packageTotals.pkg1TreatP2);
    }
    foData.push(pkg1);

    // Package 2
    const pkg2: Record<string, string> = { Fixed_value: 'Package 2' };
    if (packageNames.pkg2) {
      pkg2['Name'] = packageNames.pkg2;
      if (this.hasValue(packageTotals.pkg2TreatP1)) pkg2['Treat_wt_P1'] = String(packageTotals.pkg2TreatP1);
      if (this.hasValue(packageTotals.pkg2TreatP2)) pkg2['Treat_wt_P2'] = String(packageTotals.pkg2TreatP2);
    }
    foData.push(pkg2);

    // VM 1 & 2
    for (let i = 0; i < 2; i++) {
      const vm: Record<string, string> = { Fixed_value: `VM ${i + 1}` };
      const row = vmRows[i];
      if (row && row.selectedValue) {
        vm['Name'] = row.selectedValue;
        if (this.hasValue(row.treat1)) vm['Treat_wt_P1'] = String(row.treat1);
        if (this.hasValue(row.treat2)) vm['Treat_wt_P2'] = String(row.treat2);
      }
      foData.push(vm);
    }

    // PPD 1 & 2
    for (let i = 0; i < 2; i++) {
      const ppd: Record<string, string> = { Fixed_value: `PPD ${i + 1}` };
      const row = ppdRows[i];
      if (row && row.ppd) {
        ppd['Name'] = row.ppd;
        if (this.hasValue(row.treat1)) ppd['Treat_wt_P1'] = String(row.treat1);
        if (this.hasValue(row.treat2)) ppd['Treat_wt_P2'] = String(row.treat2);
      }
      foData.push(ppd);
    }

    // Base oil 1-6
    for (let i = 0; i < 6; i++) {
      const bo: Record<string, string> = { Fixed_value: `Base oil ${i + 1}` };
      const row = baseOilRows[i];
      if (row && row.oil) {
        bo['Name'] = row.oil;
        if (row.batch) bo['Batch'] = row.batch;
        if (this.hasValue(row.KV100cSt)) bo['kv100_cSt'] = String(row.KV100cSt);
        if (this.hasValue(row.VI)) bo['Vi'] = String(row.VI);
        if (this.hasValue(row.ratio1)) bo['Treat_wt_ratio_P1'] = String(row.ratio1);
        if (this.hasValue(row.treat1)) bo['Treat_wt_P1'] = String(row.treat1);
        if (this.hasValue(row.ratio2)) bo['Treat_wt_ratio_P2'] = String(row.ratio2);
        if (this.hasValue(row.treat2)) bo['Treat_wt_P2'] = String(row.treat2);
      }
      foData.push(bo);
    }

    return foData;
  }

  /**
   * Captures the current blend state from SharedService observables.
   */
  private async captureCurrentState(): Promise<FavoriteBlendState> {
    const [
      baseOilRows,
      vmRows,
      ppdRows,
      retrievedPackageData,
      package1Pred1,
      package1Pred2,
      package2Pred1,
      package2Pred2,
      totalPack1Pred1,
      totalPack1Pred2,
      totalPack2Pred1,
      totalPack2Pred2,
      dilPack1Pred1,
      dilPack1Pred2,
      dilPack2Pred1,
      dilPack2Pred2,
      modInpDi,
      modInpVm,
      modInpPpd,
      modInpBo,
      modInpColVm,
      modInpColBo,
      modInpColDi,
      modInpDi2,
      modInpVm2,
      modInpPpd2,
      modInpBo2,
      modInpColVm2,
      modInpColBo2,
      modInpColDi2,
    ] = await Promise.all([
      firstValueFrom(this.sharedService.baseOilSelectedData$),
      firstValueFrom(this.sharedService.vmSelectedData$),
      firstValueFrom(this.sharedService.ppdSelectedData$),
      firstValueFrom(this.sharedService.retrievedPackageData$),
      firstValueFrom(this.sharedService.package_1_pred_1$),
      firstValueFrom(this.sharedService.package_1_pred_2$),
      firstValueFrom(this.sharedService.package_2_pred_1$),
      firstValueFrom(this.sharedService.package_2_pred_2$),
      firstValueFrom(this.sharedService.total_package_1_pred_1$),
      firstValueFrom(this.sharedService.total_package_1_pred_2$),
      firstValueFrom(this.sharedService.total_package_2_pred_1$),
      firstValueFrom(this.sharedService.total_package_2_pred_2$),
      firstValueFrom(this.sharedService.total_dil_per_package_1_pred_1$),
      firstValueFrom(this.sharedService.total_dil_per_package_1_pred_2$),
      firstValueFrom(this.sharedService.total_dil_per_package_2_pred_1$),
      firstValueFrom(this.sharedService.total_dil_per_package_2_pred_2$),
      firstValueFrom(this.sharedService.mod_inp_di$),
      firstValueFrom(this.sharedService.mod_inp_vm$),
      firstValueFrom(this.sharedService.mod_inp_ppd$),
      firstValueFrom(this.sharedService.mod_inp_bo$),
      firstValueFrom(this.sharedService.mod_inp_col_vm$),
      firstValueFrom(this.sharedService.mod_inp_col_bo$),
      firstValueFrom(this.sharedService.mod_inp_col_di$),
      firstValueFrom(this.sharedService.mod_inp_di2$),
      firstValueFrom(this.sharedService.mod_inp_vm2$),
      firstValueFrom(this.sharedService.mod_inp_ppd2$),
      firstValueFrom(this.sharedService.mod_inp_bo2$),
      firstValueFrom(this.sharedService.mod_inp_col_vm2$),
      firstValueFrom(this.sharedService.mod_inp_col_bo2$),
      firstValueFrom(this.sharedService.mod_inp_col_di2$),
    ]);

    // Package names from retrievedPackageData: [0][0] = pkg1 name, [1][0] = pkg2 name
    const pkg1Name = (retrievedPackageData && retrievedPackageData[0] && Array.isArray(retrievedPackageData[0]))
      ? retrievedPackageData[0][0] || null
      : null;
    const pkg2Name = (retrievedPackageData && retrievedPackageData[1] && Array.isArray(retrievedPackageData[1]))
      ? retrievedPackageData[1][0] || null
      : null;

    // Build finishedOilData from live component state
    const finishedOilData = this.buildFinishedOilDataFromLive(
      baseOilRows || [],
      vmRows || [],
      ppdRows || [],
      {
        pkg1TreatP1: totalPack1Pred1,
        pkg1TreatP2: totalPack1Pred2,
        pkg2TreatP1: totalPack2Pred1,
        pkg2TreatP2: totalPack2Pred2,
      },
      { pkg1: pkg1Name, pkg2: pkg2Name },
    );

    // Also update the stale finishedoil_data$ so it stays in sync
    this.sharedService.set_finishedoil_data(finishedOilData);

    return {
      finishedOilData,
      package1Pred1,
      package1Pred2,
      package2Pred1,
      package2Pred2,
      wtPer11: this.hasValue(totalPack1Pred1) ? String(totalPack1Pred1) : null,
      wtPer12: this.hasValue(totalPack1Pred2) ? String(totalPack1Pred2) : null,
      wtPer21: this.hasValue(totalPack2Pred1) ? String(totalPack2Pred1) : null,
      wtPer22: this.hasValue(totalPack2Pred2) ? String(totalPack2Pred2) : null,
      dilPer11: this.hasValue(dilPack1Pred1) ? String(dilPack1Pred1) : null,
      dilPer12: this.hasValue(dilPack1Pred2) ? String(dilPack1Pred2) : null,
      dilPer21: this.hasValue(dilPack2Pred1) ? String(dilPack2Pred1) : null,
      dilPer22: this.hasValue(dilPack2Pred2) ? String(dilPack2Pred2) : null,
      modInpDi,
      modInpVm,
      modInpPpd,
      modInpBo,
      modInpColVm,
      modInpColBo,
      modInpColDi,
      modInpDi2,
      modInpVm2,
      modInpPpd2,
      modInpBo2,
      modInpColVm2,
      modInpColBo2,
      modInpColDi2,
      shareableLink: null,
    };
  }
}
